'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, MapPin, Phone, Navigation, ChevronRight, CheckCircle2, Locate, Hospital, FlaskConical, HeartPulse, HelpCircle } from 'lucide-react';
import {
  centres, searchCentres, getCentreTypeLabel,
  type Centre,
} from '@/data/locations';
import { buildWhatsAppUrl, whatsAppMessages } from '@/data/hospitalConfig';
import { trackEvent } from '@/lib/analytics';


interface LocationFinderProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCentre: (centre: Centre) => void;
  selectedCentreId?: string;
}

const typeIconMap: Record<Centre['type'], React.ElementType> = {
  'super-clinic': Hospital,
  'nursing-home': HeartPulse,
  'collection-center': FlaskConical,
  'help-center': HelpCircle,
};

const typeColorMap: Record<Centre['type'], string> = {
  'super-clinic': 'text-primary',
  'nursing-home': 'text-rose-400',
  'collection-center': 'text-sky-400',
  'help-center': 'text-emerald-400',
};

const typeBadgeMap: Record<Centre['type'], string> = {
  'super-clinic': 'bg-primary/10 text-primary border-primary/20',
  'nursing-home': 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  'collection-center': 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  'help-center': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};

const SEARCH_CITIES = [
  'Burhanpur', 'Khandwa', 'Dharni', 'Khargone',
  'Shahpur', 'Khaknar', 'Raver', 'Sanawad', 'Phopnar',
];

function haversineDistance(
  lat1: number, lon1: number,
  lat2: number, lon2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Official WhatsApp SVG brand icon — inline for LocationFinder */
function WhatsAppIcon() {
  return (
    <svg width={11} height={11} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function LocationFinder({
  isOpen,
  onClose,
  onSelectCentre,
  selectedCentreId,
}: LocationFinderProps) {
  const [query, setQuery] = useState('');
  const [filtered, setFiltered] = useState(centres);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Filter on query change
  useEffect(() => {
    const results = searchCentres(query);
    if (userCoords) {
      const withDist = results.map((c) => ({
        centre: c,
        dist:
          c.latitude && c.longitude
            ? haversineDistance(userCoords.lat, userCoords.lng, c.latitude, c.longitude)
            : Infinity,
      }));
      withDist.sort((a, b) => a.dist - b.dist);
      setFiltered(withDist.map((x) => x.centre));
    } else {
      setFiltered(results);
    }
  }, [query, userCoords]);

  // Focus search on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 300);
    } else {
      setQuery('');
      setLocError(null);
    }
  }, [isOpen]);

  const handleUseLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocError('Location not supported by your browser.');
      return;
    }
    setLocating(true);
    setLocError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      () => {
        setLocError('Location permission denied. Showing all centres.');
        setLocating(false);
      },
      { timeout: 8000 }
    );
  }, []);

  const getDistance = useCallback(
    (centre: Centre): string | null => {
      if (!userCoords || !centre.latitude || !centre.longitude) return null;
      const d = haversineDistance(
        userCoords.lat, userCoords.lng,
        centre.latitude, centre.longitude
      );
      return d < 1 ? `${Math.round(d * 1000)} m away` : `${d.toFixed(1)} km away`;
    },
    [userCoords]
  );

  const handleSelect = (centre: Centre) => {
    onSelectCentre(centre);
    onClose();
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const panelVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.97 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', damping: 28, stiffness: 280 } },
    exit: { opacity: 0, y: 30, scale: 0.97, transition: { duration: 0.22 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: (i: number) => ({
      opacity: 1, y: 0,
      transition: { delay: i * 0.05, duration: 0.35, ease: 'easeOut' },
    }),
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={overlayVariants}
          transition={{ duration: 0.25 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Panel */}
          <motion.div
            className="relative w-full md:max-w-2xl md:mx-4 max-h-[92vh] md:max-h-[88vh] flex flex-col rounded-t-3xl md:rounded-2xl overflow-hidden"
            style={{ background: 'linear-gradient(160deg, #0F1F35 0%, #0A1628 100%)', border: '1px solid rgba(201,168,76,0.15)' }}
            variants={panelVariants}
            role="dialog"
            aria-modal="true"
            aria-label="Find an All Is Well Centre"
          >
            {/* Header */}
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-white/5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-xs font-semibold tracking-widest text-primary/70 uppercase mb-1">
                    ALL IS WELL NETWORK
                  </p>
                  <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                    FIND AN ALL IS WELL CENTRE
                  </h2>
                  <p className="text-sm text-white/50 mt-1">
                    Choose a location to explore available services and care.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 transition-colors ml-4"
                  aria-label="Close location finder"
                >
                  <X size={16} className="text-white/70" />
                </button>
              </div>

              {/* Search */}
              <div className="relative">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="SEARCH CENTRES..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-primary/50 focus:bg-white/8 transition-all tracking-wider"
                  aria-label="Search All Is Well centres"
                />
              </div>

              {/* City chips */}
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
                {SEARCH_CITIES.map((city) => (
                  <button
                    key={city}
                    onClick={() => setQuery(city)}
                    className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full border transition-all ${
                      query.toLowerCase() === city.toLowerCase()
                        ? 'bg-primary/20 border-primary/50 text-primary' :'bg-white/5 border-white/10 text-white/50 hover:border-white/25 hover:text-white/70'
                    }`}
                  >
                    {city}
                  </button>
                ))}
              </div>

              {/* Use My Location */}
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={handleUseLocation}
                  disabled={locating}
                  className="flex items-center gap-2 text-xs font-semibold tracking-wider text-primary/80 hover:text-primary transition-colors disabled:opacity-50"
                  aria-label="Use my location to find nearest centres"
                >
                  <Locate size={13} className={locating ? 'animate-spin' : ''} />
                  {locating ? 'LOCATING...' : 'USE MY LOCATION'}
                </button>
                {userCoords && (
                  <span className="text-xs text-emerald-400 flex items-center gap-1">
                    <CheckCircle2 size={11} />
                    Sorted by distance
                  </span>
                )}
                {locError && (
                  <span className="text-xs text-white/40">{locError}</span>
                )}
              </div>
            </div>

            {/* Centre List */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {filtered.length === 0 ? (
                <div className="text-center py-12 text-white/30 text-sm">
                  No centres found for &quot;{query}&quot;
                </div>
              ) : (
                filtered.map((centre, i) => {
                  const TypeIcon = typeIconMap[centre.type];
                  const dist = getDistance(centre);
                  const isSelected = centre.id === selectedCentreId;

                  return (
                    <motion.div
                      key={centre.id}
                      custom={i}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      className={`group relative rounded-xl border transition-all duration-300 cursor-pointer overflow-hidden ${
                        isSelected
                          ? 'border-primary/60 bg-primary/5 shadow-lg shadow-primary/10'
                          : 'border-white/8 bg-white/3 hover:border-primary/30 hover:bg-white/5 hover:shadow-md hover:shadow-primary/5'
                      }`}
                      onClick={() => handleSelect(centre)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handleSelect(centre)}
                      aria-label={`Select ${centre.name}`}
                      aria-pressed={isSelected}
                    >
                      <div className="p-4">
                        <div className="flex items-start gap-3">
                          {/* Icon */}
                          <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-white/5 border border-white/8 ${typeColorMap[centre.type]}`}>
                            <TypeIcon size={18} />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <h3 className="text-sm font-semibold text-white leading-tight">
                                  {centre.name}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${typeBadgeMap[centre.type]}`}>
                                    {getCentreTypeLabel(centre.type)}
                                  </span>
                                  {dist && (
                                    <span className="text-xs text-emerald-400 flex items-center gap-1">
                                      <Navigation size={9} />
                                      {dist}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {isSelected && (
                                <CheckCircle2 size={16} className="text-primary flex-shrink-0 mt-0.5" />
                              )}
                            </div>

                            {/* Location */}
                            <div className="flex items-center gap-1 mt-2 text-xs text-white/40">
                              <MapPin size={10} />
                              <span>{centre.locality ? `${centre.locality}, ` : ''}{centre.city}</span>
                            </div>

                            {/* Dept count */}
                            <p className="text-xs text-white/30 mt-1">
                              {centre.departmentIds.length} department{centre.departmentIds.length !== 1 ? 's' : ''} available
                            </p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSelect(centre); }}
                            className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
                            aria-label={`View ${centre.name}`}
                          >
                            VIEW CENTRE
                            <ChevronRight size={12} />
                          </button>

                          {centre.latitude && centre.longitude && (
                            <a
                              href={`https://maps.google.com/?q=${centre.latitude},${centre.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors ml-3"
                              aria-label={`Get directions to ${centre.name}`}
                            >
                              <Navigation size={11} />
                              GET DIRECTIONS
                            </a>
                          )}

                          {centre.phone && (
                            <a
                              href={`tel:${centre.phone.replace(/\s/g, '')}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors ml-auto"
                              aria-label={`Call ${centre.name}`}
                            >
                              <Phone size={11} />
                              CALL
                            </a>
                          )}

                          {/* WhatsApp — always available via official number with centre context */}
                          <a
                            href={buildWhatsAppUrl(whatsAppMessages.centre(centre.name))}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => { e.stopPropagation(); trackEvent({ event: 'whatsapp_clicked', source: 'location-finder', centreId: centre.id }); }}
                            className="flex items-center gap-1.5 text-xs text-white/40 hover:text-green-400 transition-colors"
                            aria-label={`WhatsApp enquiry for ${centre.name}`}
                          >
                            <WhatsAppIcon />
                            WHATSAPP
                          </a>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-white/5 bg-black/20">
              <p className="text-xs text-white/25 text-center">
                {centres.length} All Is Well centres across Madhya Pradesh &amp; Maharashtra
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
